import { createSharedStore } from '../state/create-store'
import { type BillingPeriod, DEFAULT_PLAN_ID, MAX_CHILDREN, type PlanId } from './catalogue'
import { readPlanLink } from './link'
import { consumePurchase } from './purchase'
import { type CheckoutDraft, loadDraft, saveDraft } from './storage'

/**
 * The checkout form's state, shared by every Checkout mount on the page.
 *
 * Module scope for the same reason as the session: each mount is its own
 * React root, so two Checkout embeds on one page (a hero and a footer CTA,
 * say) can only stay coherent through state held outside React. One store
 * means one draft and one truth — a second embed mirrors the first instead of
 * clobbering its sessionStorage draft with a stale copy of the whole form.
 */

export interface Child {
  /** Stable render key — an index would recycle DOM state when a card is removed. */
  key: number
  name: string
}

export interface CheckoutFormState {
  step: 'plan' | 'details'
  billing: BillingPeriod
  planId: PlanId
  children: Child[]
  /**
   * True once the visitor has engaged with checkout at all — a draft exists,
   * or they interact in this pagelife. The store's default state includes one
   * (empty) child, so the CartButton badge keys off this flag rather than
   * `children.length`, or every visitor who never touched checkout would see
   * a phantom "1" in the nav.
   */
  started: boolean
  /**
   * The child added by the latest "Add a sibling", until its card takes
   * focus. A one-shot: consuming it keeps later step re-entries (Edit →
   * Continue remounts the whole details form) from re-stealing focus.
   */
  pendingFocusKey: number | null

  setStep(step: 'plan' | 'details'): void
  setBilling(billing: BillingPeriod): void
  /** Settle on a plan and open step 2: a card click, or Continue with the one already chosen. */
  choosePlan(planId: PlanId): void
  addChild(): void
  removeChild(key: number): void
  renameChild(key: number, name: string): void
  consumeFocus(): void
}

let nextKey = 0
const newChild = (name = ''): Child => ({ key: nextKey++, name })

/** The form as a draft left it, or untouched without one. */
function stateFrom(draft: CheckoutDraft | null) {
  return {
    step: draft?.step ?? ('plan' as const),
    billing: draft?.billing ?? ('annual' as const),
    planId: draft?.planId ?? DEFAULT_PLAN_ID,
    children: (draft?.childNames ?? ['']).map((name) => newChild(name)),
    started: draft !== null,
    pendingFocusKey: null,
  }
}

/**
 * Hydrate from the sessionStorage draft — synchronously, like the session —
 * unless a purchase has completed since it was written, which ends it.
 */
function initialState() {
  consumePurchase()
  return stateFrom(loadDraft())
}

export const checkoutForm = createSharedStore<CheckoutFormState>((set, get) => {
  /** Every user-driven mutation marks the visitor as having started checkout. */
  const track = (partial: Partial<CheckoutFormState>) => set({ ...partial, started: true })

  return {
    ...initialState(),

    setStep: (step) => track({ step }),
    setBilling: (billing) => track({ billing }),
    choosePlan: (planId) => track({ planId, step: 'details' }),

    addChild: () => {
      const { children } = get()
      if (children.length >= MAX_CHILDREN) return
      const added = newChild()
      track({ children: [...children, added], pendingFocusKey: added.key })
    },

    removeChild: (key) => track({ children: get().children.filter((child) => child.key !== key) }),

    renameChild: (key, name) =>
      track({
        children: get().children.map((child) => (child.key === key ? { ...child, name } : child)),
      }),

    // Plain set: focus housekeeping is not engagement.
    consumeFocus: () => set({ pendingFocusKey: null }),
  }
})

export const useCheckoutForm = checkoutForm.useStore

// Persist every change the visitor makes. subscribe() never fires at module
// load, and an untouched form — the default, or a reset after a purchase —
// has nothing to draft; saving one would count its empty child in the nav.
checkoutForm.subscribe((state) => {
  if (!state.started) return
  saveDraft({
    step: state.step,
    billing: state.billing,
    planId: state.planId,
    childNames: state.children.map((child) => child.name),
  })
})

/**
 * A pricing-page link (`?plan=online-only&billing=monthly`) answers step 1 on
 * arrival. Applied through `set` after the subscription above, so it persists
 * like any other choice — the nav badge, a detour around the site and a later
 * return all agree with what was clicked. A link without a billing period
 * keeps the draft's.
 */
function applyPlanLink(): void {
  const link = readPlanLink(window.location.search)
  if (!link) return
  const { planId, billing = checkoutForm.get().billing } = link
  checkoutForm.set({ planId, billing, step: 'details', started: true })
}

applyPlanLink()

/**
 * A purchase can complete after this page ran: in this tab, with the visitor
 * returning by Back — which may restore the page from the back/forward cache,
 * store and form exactly as they were, without running the bundle again — or
 * in another tab, with the visitor switching back to this one. The reset does
 * not re-apply a `?plan=` link in the address: that click led to the purchase.
 */
function discardDraftIfPurchased(): void {
  if (consumePurchase()) checkoutForm.set(stateFrom(null))
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted) discardDraftIfPurchased()
})
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') discardDraftIfPurchased()
})

/**
 * Re-read the draft and the URL into the store — the checkout twin of
 * rehydrateSession. Tests use it to reset between cases and to simulate a
 * fresh page load.
 */
export function rehydrateCheckoutForm(): void {
  checkoutForm.set(initialState())
  applyPlanLink()
}
